/**
 * Storefront chat (boxed chat v2, one room per environment; docs/chat.md): the room's history
 * query, the reducer that folds realtime events into it, the send/delete/report mutations, the
 * heartbeat, and the per-viewer conveniences kept in localStorage (the ignore list, the rules
 * agreement). Hooks and helpers only; the components are under `components/chat/`.
 */
import {
  CHAT_HISTORY_LIMIT,
  type ChatEvent,
  type ChatHistoryResponse,
  type ChatMessage,
  type ChatReportCategory,
  chatChannel,
  parseChatEvent,
} from "@loot-os/api-contract";
import { LootApiError } from "@loot-os/client";
import { keepPreviousData, queryOptions, useMutation, useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useState, useSyncExternalStore } from "react";

import { lootFetch, qk } from "./queries.js";
import { queryClient } from "./queryClient.js";
import { useShopper } from "./shopper.js";
import { bypassNextConfigCache, useTenant } from "./tenant.js";
import { getRealtimeClient } from "./useBattleChannel.js";

/** The one room today (`CHAT_ROOM_IDS`). */
export const DEFAULT_CHAT_ROOM = "general";

/** How often the room is refetched when there is no live subscription to tell us about changes. */
export const CHAT_POLL_MS = 5_000;

/** Heartbeat cadence (boxed's): 45 s with ±20% jitter, only while the page is visible. */
export const CHAT_HEARTBEAT_MS = 45_000;

// The room's history is public (a signed-out shopper reads it too), but `viewer` in the answer
// is the signed-in shopper's own mute, so the key names the viewer.
export const chatKeys = {
  all: ["chat"] as const,
  room: (roomId: string, viewer: string) => ["chat", roomId, viewer] as const,
};

// ---- the reducer ---------------------------------------------------------------------

/** Orders by id (ULIDs sort by time) and keeps the newest `CHAT_HISTORY_LIMIT`. */
function capNewest(messages: ChatMessage[]): ChatMessage[] {
  const sorted = [...messages].sort((a, b) =>
    a.messageId < b.messageId ? -1 : a.messageId > b.messageId ? 1 : 0,
  );
  return sorted.length > CHAT_HISTORY_LIMIT ? sorted.slice(-CHAT_HISTORY_LIMIT) : sorted;
}

/**
 * Folds one realtime event into a room's history. Idempotent, so replaying an event the history
 * already reflects changes nothing: a message is keyed by id, a delete removes and scrubs.
 */
export function applyChatEvent(state: ChatHistoryResponse, event: ChatEvent): ChatHistoryResponse {
  switch (event.type) {
    case "message": {
      if (event.message.roomId !== state.roomId) return state;
      const others = state.messages.filter((m) => m.messageId !== event.message.messageId);
      return {
        ...state,
        messages: capNewest([...others, event.message]),
        headcount: event.headcount,
      };
    }
    case "message-deleted": {
      if (event.roomId !== state.roomId) return state;
      return {
        ...state,
        messages: state.messages
          .filter((m) => m.messageId !== event.messageId)
          .map((m) =>
            m.replyTo?.messageId === event.messageId && !m.replyTo.deleted
              ? {
                  ...m,
                  replyTo: {
                    messageId: m.replyTo.messageId,
                    endUserId: m.replyTo.endUserId,
                    displayName: m.replyTo.displayName,
                    deleted: true,
                  },
                }
              : m,
          ),
      };
    }
    case "presence":
      if (event.roomId !== state.roomId) return state;
      return { ...state, headcount: event.headcount };
  }
}

/**
 * Events received while a history fetch was in flight may predate what that fetch read, so each
 * room keeps the last few seconds of message events and re-applies them to every fetched answer.
 * Presence is left out: a fetched headcount is newer than a replayed one.
 */
const RECENT_EVENT_MS = 30_000;
const recentEvents = new Map<string, Array<{ at: number; event: ChatEvent }>>();

function rememberEvent(roomKey: string, event: ChatEvent): void {
  if (event.type === "presence") return;
  const now = Date.now();
  const list = (recentEvents.get(roomKey) ?? []).filter((e) => now - e.at < RECENT_EVENT_MS);
  list.push({ at: now, event });
  recentEvents.set(roomKey, list);
}

/** Re-applies the room's recent events on top of a freshly fetched history. Exported for tests. */
export function replayRecentEvents(
  history: ChatHistoryResponse,
  events: ReadonlyArray<ChatEvent>,
): ChatHistoryResponse {
  return events.reduce((state, event) => {
    // A replayed message must not roll the fetched headcount back.
    if (event.type === "message") {
      return { ...applyChatEvent(state, event), headcount: state.headcount };
    }
    return applyChatEvent(state, event);
  }, history);
}

/** Tests only. */
export function resetRecentChatEvents(): void {
  recentEvents.clear();
}

// ---- queries ---------------------------------------------------------------------------

export const chatMessagesQueryOptions = (opts: { roomId: string; viewer: string; poll: boolean }) =>
  queryOptions({
    queryKey: chatKeys.room(opts.roomId, opts.viewer),
    queryFn: async () => {
      const history = await lootFetch<ChatHistoryResponse>(
        `/chat/rooms/${encodeURIComponent(opts.roomId)}/messages`,
      );
      const now = Date.now();
      const recent = (recentEvents.get(opts.roomId) ?? [])
        .filter((e) => now - e.at < RECENT_EVENT_MS)
        .map((e) => e.event);
      return replayRecentEvents(history, recent);
    },
    staleTime: 2_000,
    retry: false,
    refetchInterval: opts.poll ? CHAT_POLL_MS : false,
  });

/** Applies an event to every cached copy of the room (one per viewer key). */
function applyToRoom(roomId: string, event: ChatEvent): void {
  queryClient.setQueriesData<ChatHistoryResponse>({ queryKey: ["chat", roomId] }, (prev) =>
    prev ? applyChatEvent(prev, event) : prev,
  );
}

/**
 * The room: its history, kept current by the room's realtime channel when the stage has one
 * (and by polling when it does not, or while the subscription is not live). History is refetched
 * whenever the subscription goes live, because AppSync does not replay what a dropped socket
 * missed.
 */
export function useChatRoom(roomId: string = DEFAULT_CHAT_ROOM) {
  const { config } = useTenant();
  const { sessionToken, user } = useShopper();
  const client = getRealtimeClient(config.realtime);
  const environmentId = config.environmentId;
  const [live, setLive] = useState(false);
  const viewer = user && sessionToken ? `user:${user.id}` : "anon";

  useEffect(() => {
    if (!client) return;
    const unsubscribe = client.subscribe(
      chatChannel(environmentId, roomId),
      (raw) => {
        const event = parseChatEvent(raw);
        if (!event) return;
        rememberEvent(roomId, event);
        applyToRoom(roomId, event);
      },
      (status) => {
        setLive(status === "live");
        if (status === "live") {
          void queryClient.invalidateQueries({ queryKey: ["chat", roomId] });
        }
      },
    );
    return () => {
      unsubscribe();
      setLive(false);
    };
  }, [client, environmentId, roomId]);

  const query = useQuery(chatMessagesQueryOptions({ roomId, viewer, poll: !live }));
  useEffect(() => noteChatDisabled(query.error), [query.error]);
  return { ...query, live };
}

function isChatDisabled(err: unknown): boolean {
  return err instanceof LootApiError && (err.code as string) === "chat_disabled";
}

/**
 * The API said chat is off for this store (`chat_disabled`): recheck the room, which hides the
 * panel and its tab through `useChatAvailable`, and refetch the store's config past the browser's
 * cache so the rest of the page catches up too.
 */
export function noteChatDisabled(err: unknown): void {
  if (!isChatDisabled(err)) return;
  void queryClient.invalidateQueries({ queryKey: ["chat"] });
  bypassNextConfigCache();
  void queryClient.invalidateQueries({ queryKey: qk.tenant });
}

/** How often a page with chat showing rechecks that it is still on (a signed-out reader with a
 * live socket otherwise never asks the API again). */
const CHAT_AVAILABILITY_RECHECK_MS = 60_000;

/**
 * Whether the store shows chat at all: the config fetched on this page load says so, and the API,
 * which reads the switch from the database rather than a cache, has answered the room. Until it
 * answers nothing renders, and a `chat_disabled` answer, now or later, removes the tab and the
 * panel, so a store with chat off never shows either, whatever the config caches still say. Any
 * other failure (a blip) keeps what is showing. It shares the room's history query.
 */
export function useChatAvailable(): boolean {
  const { hasFeature, configFresh } = useTenant();
  const { sessionToken, user } = useShopper();
  const configOn = configFresh && hasFeature("chat");
  const viewer = user && sessionToken ? `user:${user.id}` : "anon";
  const query = useQuery({
    ...chatMessagesQueryOptions({ roomId: DEFAULT_CHAT_ROOM, viewer, poll: false }),
    enabled: configOn,
    // Signing in changes the key; keep showing chat while the new viewer's copy loads.
    placeholderData: keepPreviousData,
    refetchInterval: CHAT_AVAILABILITY_RECHECK_MS,
  });
  return configOn && query.data !== undefined && !isChatDisabled(query.error);
}

/**
 * Who's in the room, from whatever copy of it is cached (`useChatAvailable` keeps one loaded):
 * undefined until the room has answered. Reads only; never fetches.
 */
export function useChatHeadcount(roomId: string = DEFAULT_CHAT_ROOM): number | undefined {
  const { sessionToken, user } = useShopper();
  const viewer = user && sessionToken ? `user:${user.id}` : "anon";
  const query = useQuery({
    ...chatMessagesQueryOptions({ roomId, viewer, poll: false }),
    enabled: false,
    select: (room) => room.headcount,
  });
  return query.data;
}

// ---- mutations ---------------------------------------------------------------------------

export function useSendChatMessage(roomId: string = DEFAULT_CHAT_ROOM) {
  return useMutation({
    mutationFn: (body: { text: string; replyToMessageId?: string }) =>
      lootFetch<{ message: ChatMessage }>(`/chat/rooms/${encodeURIComponent(roomId)}/messages`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    // The channel echoes it too; applying it now keeps a polling room from lagging the sender.
    onSuccess: ({ message }) => {
      queryClient.setQueriesData<ChatHistoryResponse>({ queryKey: ["chat", roomId] }, (prev) =>
        prev ? applyChatEvent(prev, { type: "message", message, headcount: prev.headcount }) : prev,
      );
    },
    onError: (err) => {
      noteChatDisabled(err);
      // A mute the page did not know about: refetch so `viewer.muted` shows the notice.
      // The published client's ErrorCode predates chat, so the code is compared as a string.
      if (err instanceof LootApiError && (err.code as string) === "chat_muted") {
        void queryClient.invalidateQueries({ queryKey: ["chat", roomId] });
      }
    },
  });
}

export function useDeleteChatMessage(roomId: string = DEFAULT_CHAT_ROOM) {
  return useMutation({
    mutationFn: (messageId: string) => fetchChatDelete(roomId, messageId),
    onSuccess: (_res, messageId) =>
      applyToRoom(roomId, { type: "message-deleted", roomId, messageId }),
  });
}

/** DELETE answers 204 with no body. */
async function fetchChatDelete(roomId: string, messageId: string): Promise<void> {
  await lootFetch<void>(
    `/chat/rooms/${encodeURIComponent(roomId)}/messages/${encodeURIComponent(messageId)}`,
    { method: "DELETE" },
  );
}

export function useReportChatMessage(roomId: string = DEFAULT_CHAT_ROOM) {
  return useMutation({
    mutationFn: (args: { messageId: string; category: ChatReportCategory; description?: string }) =>
      lootFetch<{ reportId: string }>(
        `/chat/rooms/${encodeURIComponent(roomId)}/messages/${encodeURIComponent(args.messageId)}/report`,
        {
          method: "POST",
          body: JSON.stringify({
            category: args.category,
            ...(args.description ? { description: args.description } : {}),
          }),
        },
      ),
  });
}

// ---- heartbeat ---------------------------------------------------------------------------

/** 45 s ± 20%. Exported for tests. */
export function nextHeartbeatDelayMs(random: () => number = Math.random): number {
  return Math.round(CHAT_HEARTBEAT_MS * (0.8 + random() * 0.4));
}

/**
 * Tells the room the signed-in shopper is here, on mount and then about every 45 s while the page
 * is visible; a hidden tab stops, and coming back beats at once. The answer's headcount updates
 * the cached room.
 */
export function useChatHeartbeat(roomId: string, enabled: boolean): void {
  useEffect(() => {
    if (!enabled) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let stopped = false;

    const beat = async () => {
      if (stopped || document.visibilityState !== "visible") return;
      try {
        const { headcount } = await lootFetch<{ headcount: number }>(
          `/chat/rooms/${encodeURIComponent(roomId)}/heartbeat`,
          { method: "POST" },
        );
        applyToRoom(roomId, { type: "presence", roomId, headcount });
      } catch (err) {
        // Presence is best-effort: the next beat tries again, unless chat was switched off.
        noteChatDisabled(err);
      }
    };
    const schedule = () => {
      if (stopped) return;
      timer = setTimeout(async () => {
        await beat();
        schedule();
      }, nextHeartbeatDelayMs());
    };
    const onVisibility = () => {
      if (document.visibilityState !== "visible") return;
      if (timer) clearTimeout(timer);
      void beat().then(schedule);
    };

    void beat().then(schedule);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [roomId, enabled]);
}

// ---- per-viewer conveniences (localStorage) ------------------------------------------------

function readStorage(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string | null): void {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch {
    // Private windows and blocked storage: the preference lasts for this page only.
  }
}

/** A tiny localStorage-backed store every component reading the same key shares. */
function storageStore<T>(
  key: string,
  parse: (raw: string | null) => T,
  serialize: (v: T) => string,
) {
  const listeners = new Set<() => void>();
  let value = parse(readStorage(key));
  return {
    get: () => value,
    set(next: T) {
      value = next;
      writeStorage(key, serialize(next));
      for (const listener of listeners) listener();
    },
    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

export interface IgnoredChatUser {
  endUserId: string;
  displayName: string;
}

function parseIgnored(raw: string | null): IgnoredChatUser[] {
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (u): u is IgnoredChatUser =>
        typeof u === "object" &&
        u !== null &&
        typeof (u as IgnoredChatUser).endUserId === "string" &&
        typeof (u as IgnoredChatUser).displayName === "string",
    );
  } catch {
    return [];
  }
}

const ignoredStore = storageStore("loot-chat-ignored", parseIgnored, (v) => JSON.stringify(v));

/** The viewer's local ignore list: their messages are hidden in this browser only. */
export function useIgnoredUsers() {
  const ignored = useSyncExternalStore(ignoredStore.subscribe, ignoredStore.get, ignoredStore.get);
  const isIgnored = useCallback(
    (endUserId: string) => ignored.some((u) => u.endUserId === endUserId),
    [ignored],
  );
  const ignore = useCallback((user: IgnoredChatUser) => {
    const current = ignoredStore.get();
    if (current.some((u) => u.endUserId === user.endUserId)) return;
    ignoredStore.set([...current, user]);
  }, []);
  const unignore = useCallback((endUserId: string) => {
    ignoredStore.set(ignoredStore.get().filter((u) => u.endUserId !== endUserId));
  }, []);
  return { ignored, isIgnored, ignore, unignore };
}

const rulesStore = storageStore(
  "loot-chat-rules-agreed",
  (raw) => raw === "1",
  (v) => (v ? "1" : ""),
);

/** Whether this browser has agreed to the chat rules (boxed's `chat-rules-agreed`). */
export function useChatRulesAgreed(): [boolean, () => void] {
  const agreed = useSyncExternalStore(rulesStore.subscribe, rulesStore.get, rulesStore.get);
  return [agreed, useCallback(() => rulesStore.set(true), [])];
}

const openListeners = new Set<() => void>();
let chatOpen = false;
const openStore = {
  get: () => chatOpen,
  subscribe(listener: () => void) {
    openListeners.add(listener);
    return () => openListeners.delete(listener);
  },
};

/**
 * Opens or closes the chat drawer: the header's launcher on a wide screen, the tab bar's Chat on
 * a phone. It starts closed on every page load, so the page leads.
 */
export function setChatOpen(open: boolean): void {
  chatOpen = open;
  for (const listener of openListeners) listener();
}

/** Whether the chat drawer is open. */
export function useChatOpen(): boolean {
  return useSyncExternalStore(openStore.subscribe, openStore.get, openStore.get);
}

// ---- words ----------------------------------------------------------------------------------

const REJECT_TEXT: Record<string, string> = {
  empty: "Type a message first.",
  too_long: "That message is too long.",
  profanity: "That message was blocked. Keep it clean.",
  link: "Links are not allowed in chat.",
};

/** When a mute ends, in the viewer's locale; null for an indefinite mute. */
export function muteUntilLabel(expiresAt: string | undefined | null): string | null {
  if (!expiresAt) return null;
  const at = new Date(expiresAt);
  if (Number.isNaN(at.getTime())) return null;
  return at.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

/** What to tell the shopper when a chat action failed. */
export function toUserFacingChatError(err: unknown): string {
  if (err instanceof LootApiError) {
    // The published client's ErrorCode predates chat, so the code is compared as a string.
    const code: string = err.code;
    switch (code) {
      case "chat_muted": {
        const until = muteUntilLabel(
          typeof err.details?.expiresAt === "string" ? err.details.expiresAt : null,
        );
        return until ? `You are muted in chat until ${until}.` : "You are muted in chat.";
      }
      case "chat_message_rejected": {
        const reason = typeof err.details?.reason === "string" ? err.details.reason : "";
        return REJECT_TEXT[reason] ?? "That message was not sent.";
      }
      case "rate_limited":
        return "Slow down — try again in a moment.";
      case "username_required":
        return "Choose a username before chatting.";
      case "account_locked":
        return "Your account is locked, so you cannot chat.";
      case "chat_disabled":
        return "Chat is turned off right now.";
      case "platform_switch_disabled":
        return "Chat is paused right now. Try again later.";
      case "chat_message_not_found":
        return "That message is gone.";
      case "forbidden":
        return "You can only delete your own messages.";
      default:
        return err.message || "Something went wrong. Try again.";
    }
  }
  return "Something went wrong. Try again.";
}

/** Whether the text mentions the viewer as `@username`, case-insensitive, as a whole word. */
export function mentionsViewer(text: string, username: string | null | undefined): boolean {
  if (!username) return false;
  const escaped = username.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return new RegExp(`(^|[^\\w])@${escaped}(?![\\w])`, "i").test(text);
}
